export type UserRole = 'architect' | 'admin' | 'moderator' | 'user' | 'viewer';

export interface UserPermissions {
  braindump: {
    enabled: boolean;
    dailyLimit?: number;
    monthlyLimit?: number;
    maxTokensPerRequest?: number;
  };
  tasks: {
    canCreate: boolean;
    canEditOwn: boolean;
    canEditAll: boolean;
    canDeleteOwn: boolean;
    canDeleteAll: boolean;
    canAssign: boolean;
    maxSubtaskDepth?: number;
  };
  features: {
    aiEnhancement: boolean;
    timeTracking: boolean;
    advancedSearch: boolean;
    bulkOperations: boolean;
    exportData: boolean;
  };
  admin: {
    canManageUsers: boolean;
    canViewAuditLogs: boolean;
    canManageRoles: boolean;
    canAccessSystemSettings: boolean;
    canOverrideRestrictions: boolean;
  };
}

export interface RoleConfig {
  role: UserRole;
  displayName: string;
  description: string;
  color: string;
  icon: string;
  permissions: UserPermissions;
  canManageRoles: boolean;
  canViewAllData: boolean;
  canOverrideRestrictions: boolean;
  hierarchy: number; // 1 = highest (architect), 5 = lowest (viewer)
}

export const ROLE_CONFIGS: Record<UserRole, RoleConfig> = {
  architect: {
    role: 'architect',
    displayName: 'Architect',
    description: 'System architect with absolute control and oversight',
    color: 'purple',
    icon: '🏛️',
    hierarchy: 1,
    permissions: {
      braindump: {
        enabled: true,
        // No limits for architect
      },
      tasks: {
        canCreate: true,
        canEditOwn: true,
        canEditAll: true,
        canDeleteOwn: true,
        canDeleteAll: true,
        canAssign: true,
      },
      features: {
        aiEnhancement: true,
        timeTracking: true,
        advancedSearch: true,
        bulkOperations: true,
        exportData: true,
      },
      admin: {
        canManageUsers: true,
        canViewAuditLogs: true,
        canManageRoles: true,
        canAccessSystemSettings: true,
        canOverrideRestrictions: true,
      },
    },
    canManageRoles: true,
    canViewAllData: true,
    canOverrideRestrictions: true,
  },
  admin: {
    role: 'admin',
    displayName: 'Administrator',
    description: 'System administrator with elevated privileges',
    color: 'blue',
    icon: '👨‍💼',
    hierarchy: 2,
    permissions: {
      braindump: {
        enabled: true,
        dailyLimit: 50,
        monthlyLimit: 1000,
        maxTokensPerRequest: 4000,
      },
      tasks: {
        canCreate: true,
        canEditOwn: true,
        canEditAll: true,
        canDeleteOwn: true,
        canDeleteAll: false, // Admins can't delete others' tasks unless architect grants permission
        canAssign: true,
        maxSubtaskDepth: 5,
      },
      features: {
        aiEnhancement: true,
        timeTracking: true,
        advancedSearch: true,
        bulkOperations: true,
        exportData: true,
      },
      admin: {
        canManageUsers: true,
        canViewAuditLogs: true,
        canManageRoles: false, // Only architects can manage roles
        canAccessSystemSettings: false,
        canOverrideRestrictions: false,
      },
    },
    canManageRoles: false,
    canViewAllData: true,
    canOverrideRestrictions: false,
  },
  moderator: {
    role: 'moderator',
    displayName: 'Moderator',
    description: 'Content moderator with limited administrative privileges',
    color: 'orange',
    icon: '🛡️',
    hierarchy: 3,
    permissions: {
      braindump: {
        enabled: true,
        dailyLimit: 25,
        monthlyLimit: 500,
        maxTokensPerRequest: 3000,
      },
      tasks: {
        canCreate: true,
        canEditOwn: true,
        canEditAll: false,
        canDeleteOwn: true,
        canDeleteAll: false,
        canAssign: false,
        maxSubtaskDepth: 4,
      },
      features: {
        aiEnhancement: true,
        timeTracking: true,
        advancedSearch: true,
        bulkOperations: false,
        exportData: false,
      },
      admin: {
        canManageUsers: false,
        canViewAuditLogs: false,
        canManageRoles: false,
        canAccessSystemSettings: false,
        canOverrideRestrictions: false,
      },
    },
    canManageRoles: false,
    canViewAllData: false,
    canOverrideRestrictions: false,
  },
  user: {
    role: 'user',
    displayName: 'User',
    description: 'Standard user with basic privileges',
    color: 'green',
    icon: '👤',
    hierarchy: 4,
    permissions: {
      braindump: {
        enabled: true,
        dailyLimit: 10,
        monthlyLimit: 200,
        maxTokensPerRequest: 2000,
      },
      tasks: {
        canCreate: true,
        canEditOwn: true,
        canEditAll: false,
        canDeleteOwn: true,
        canDeleteAll: false,
        canAssign: false,
        maxSubtaskDepth: 3,
      },
      features: {
        aiEnhancement: true,
        timeTracking: true,
        advancedSearch: false,
        bulkOperations: false,
        exportData: false,
      },
      admin: {
        canManageUsers: false,
        canViewAuditLogs: false,
        canManageRoles: false,
        canAccessSystemSettings: false,
        canOverrideRestrictions: false,
      },
    },
    canManageRoles: false,
    canViewAllData: false,
    canOverrideRestrictions: false,
  },
  viewer: {
    role: 'viewer',
    displayName: 'Viewer',
    description: 'Read-only access with minimal privileges',
    color: 'gray',
    icon: '👁️',
    hierarchy: 5,
    permissions: {
      braindump: {
        enabled: false,
      },
      tasks: {
        canCreate: false,
        canEditOwn: false,
        canEditAll: false,
        canDeleteOwn: false,
        canDeleteAll: false,
        canAssign: false,
      },
      features: {
        aiEnhancement: false,
        timeTracking: false,
        advancedSearch: false,
        bulkOperations: false,
        exportData: false,
      },
      admin: {
        canManageUsers: false,
        canViewAuditLogs: false,
        canManageRoles: false,
        canAccessSystemSettings: false,
        canOverrideRestrictions: false,
      },
    },
    canManageRoles: false,
    canViewAllData: false,
    canOverrideRestrictions: false,
  },
};

/**
 * Permission utility functions
 */
export class PermissionChecker {
  static getRoleConfig(userRole: UserRole | undefined): RoleConfig {
    if (!userRole || !ROLE_CONFIGS[userRole]) {
      return ROLE_CONFIGS.viewer; // Default to viewer role for safety
    }
    return ROLE_CONFIGS[userRole];
  }

  static hasRole(userRole: UserRole | undefined, requiredRole: UserRole): boolean {
    const userConfig = PermissionChecker.getRoleConfig(userRole);
    const requiredConfig = ROLE_CONFIGS[requiredRole];
    
    return userConfig.hierarchy <= requiredConfig.hierarchy;
  }
  
  static canPerformAction(userRole: UserRole | undefined, action: keyof UserPermissions): boolean {
    const config = PermissionChecker.getRoleConfig(userRole);
    return action in config.permissions;
  }
  
  static canEditTask(userRole: UserRole | undefined, isOwner: boolean, isAssignee: boolean): boolean {
    const permissions = PermissionChecker.getRoleConfig(userRole).permissions.tasks;
    
    // Architect can edit anything
    if (userRole === 'architect') {return true;}
    
    // Can edit all tasks
    if (permissions.canEditAll) {return true;}
    
    // Can edit own tasks
    if (permissions.canEditOwn && (isOwner || isAssignee)) {return true;}
    
    return false;
  }
  
  static canDeleteTask(userRole: UserRole | undefined, isOwner: boolean): boolean {
    const permissions = PermissionChecker.getRoleConfig(userRole).permissions.tasks;
    
    // Architect can delete anything
    if (userRole === 'architect') {return true;}
    
    // Can delete all tasks
    if (permissions.canDeleteAll) {return true;}
    
    // Can delete own tasks
    if (permissions.canDeleteOwn && isOwner) {return true;}
    
    return false;
  }
  
  static getBraindumpLimits(userRole: UserRole | undefined): { dailyLimit: number; monthlyLimit: number } {
    const permissions = PermissionChecker.getRoleConfig(userRole).permissions.braindump;
    
    return {
      dailyLimit: permissions.dailyLimit ?? 999999,
      monthlyLimit: permissions.monthlyLimit ?? 999999,
    };
  }
  
  static getRoleHierarchy(userRole: UserRole | undefined): number {
    return PermissionChecker.getRoleConfig(userRole).hierarchy;
  }
  
  static isArchitect(userRole: UserRole | undefined): boolean {
    return userRole === 'architect';
  }
  
  static isAdmin(userRole: UserRole | undefined): boolean {
    return userRole === 'admin';
  }
  
  static hasElevatedPrivileges(userRole: UserRole | undefined): boolean {
    return userRole === 'architect' || userRole === 'admin';
  }
}

/**
 * Role display utilities
 */
export class RoleDisplay {
  static getBadgeColor(role: UserRole): string {
    const config = ROLE_CONFIGS[role];
    return config.color;
  }
  
  static getIcon(role: UserRole): string {
    const config = ROLE_CONFIGS[role];
    return config.icon;
  }
  
  static getDisplayName(role: UserRole): string {
    const config = ROLE_CONFIGS[role];
    return config.displayName;
  }
  
  static getDescription(role: UserRole): string {
    const config = ROLE_CONFIGS[role];
    return config.description;
  }
}
